import ActivityMediaType from 'components/Activity/ActivityMediaType';
import ApplicationSubjectItem from 'components/Application/ApplicationSubjectItem';
import EventStatus from 'core/enums/eventStatus';
import Event from 'core/models/event';
import { getContactLabel } from 'core/useCases/contacts/utils';
import { renderEventMediaText } from 'core/useCases/events/utils';
import useResponsive from 'hooks/responsive';
import Checked from 'images/icons/Checked.svg?react';
import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SBoldPrimaryText, STable } from 'styles/components';
import { computeDateWithoutTime, getCompleteDate, getTime } from 'utils/dateTimeFormat';
import EventActions from '../EventActions';
import MarkEventAsCompleteButton from '../MarkEventAsCompleteButton';
import {
    SCard,
    SCardHeader,
    SCardHeaderLeft,
    SCardLine,
    SCardLineLeft,
    SCardLineRight,
    SSubject,
    STwoLines,
} from './styles';

type EventsByDate = {
    events: Event[];
    date: Date;
    dateKey: string;
};

type EventListProps = {
    events: Event[];
};

const EventList: FC<EventListProps> = ({ events }) => {
    const { t } = useTranslation();
    const [eventsByDateList, setEventsByDateList] = useState<EventsByDate[]>([]);
    const [sortSubjectByDay, setSortSubjectByDay] = useState<Record<string, 'asc' | 'desc'>>({});
    const { isMobile } = useResponsive();

    useEffect(() => {
        const eventsByDateListCompute = _(
            events.map((e: Event) => ({ ...e, dateWithoutTime: computeDateWithoutTime(new Date(e.date)) })),
        )
            .groupBy('dateWithoutTime')
            .map((items: Array<Event & { dateWithoutTime: Date }>, dateKey) => ({
                date: new Date(dateKey),
                dateKey,
                events: _.orderBy(
                    items.map((item: Event & { dateWithoutTime: Date }) => {
                         
                        const { dateWithoutTime, ...rest } = item;

                        return rest;
                    }),
                    [(event: Event) => t(`common.${event.type}-subject.enum.${event.subject}`).toLowerCase()],
                    [sortSubjectByDay[dateKey] ?? 'asc'],
                ),
            }))
            .value();
        setEventsByDateList(eventsByDateListCompute);
    }, [events, setEventsByDateList, sortSubjectByDay, t]);

    const toggleSortSubjectForDay = (dateKey: string) => {
        setSortSubjectByDay((prev) => ({
            ...prev,
            [dateKey]: prev[dateKey] === 'asc' ? 'desc' : 'asc',
        }));
    };

    return (
        <div>
            {eventsByDateList?.map((eventsByDate: EventsByDate) =>
                isMobile ? (
                    <div key={String(eventsByDate.date)}>
                        <h2>{getCompleteDate(eventsByDate.date, t)}</h2>
                        {eventsByDate.events.map((event) => {
                            const { id, subject, mediaType, contact, date, type, status, application } = event;

                            return (
                                <SCard key={id}>
                                    <SCardHeader>
                                        <SCardHeaderLeft>
                                            { }
                                            {EventStatus.NotStarted === status && (
                                                <MarkEventAsCompleteButton eventId={id} />
                                            )}
                                            {EventStatus.Completed === status && <Checked />}
                                            <SSubject isCompleted={EventStatus.Completed === status}>
                                                {t(`common.event-type.enum.${type}`)}
                                            </SSubject>
                                        </SCardHeaderLeft>
                                        <EventActions eventId={id} />
                                    </SCardHeader>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.subject.label')}</SCardLineLeft>
                                        <SCardLineRight>
                                            { }
                                            <Link
                                                title={t(`common.${type}-subject.enum.${subject}`)}
                                                to={`/event/${id}`}
                                            >
                                                <SBoldPrimaryText>
                                                    {t(`common.${type}-subject.enum.${subject}`)}
                                                </SBoldPrimaryText>
                                            </Link>
                                            <br />
                                            {getTime(date)}
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.media-type.label')}</SCardLineLeft>
                                        <SCardLineRight>
                                            <STwoLines>
                                                <ActivityMediaType mediaType={mediaType} />
                                                {renderEventMediaText(event)}
                                            </STwoLines>
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('application.label')}</SCardLineLeft>
                                        <SCardLineRight>
                                            <ApplicationSubjectItem application={application} />
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.contact')}</SCardLineLeft>
                                        <SCardLineRight>
                                            {contact && (
                                                <>
                                                    <Link to={`/contact/${contact.id}`}>
                                                        <SBoldPrimaryText>
                                                            {getContactLabel(contact, t)}
                                                        </SBoldPrimaryText>
                                                    </Link>
                                                    <br />
                                                    <span>{contact.company}</span>
                                                </>
                                            )}
                                        </SCardLineRight>
                                    </SCardLine>
                                    <SCardLine>
                                        <SCardLineLeft>{t('common.intermediary.label')}</SCardLineLeft>
                                        <SCardLineRight>
                                            {contact?.parent ? (
                                                <Link to={`/contact/${contact.parent.id}`}>
                                                    {getContactLabel(contact.parent, t)}
                                                </Link>
                                            ) : (
                                                <span>{t('common.you')}</span>
                                            )}
                                        </SCardLineRight>
                                    </SCardLine>
                                </SCard>
                            );
                        })}
                    </div>
                ) : (
                    <div key={String(eventsByDate.date)}>
                        <h2>{getCompleteDate(eventsByDate.date, t)}</h2>
                        <STable>
                            <thead>
                                <tr>
                                    { }
                                    <th />
                                    <th>{t('common.type')}</th>
                                    <th>
                                        <button
                                            onClick={() => toggleSortSubjectForDay(eventsByDate.dateKey)}
                                            type="button"
                                        >
                                            {t('common.subject.label')}
                                            {(sortSubjectByDay[eventsByDate.dateKey] ?? 'asc') === 'asc' ? '▼' : '▲'}
                                        </button>
                                    </th>
                                    <th>{t('application.label')}</th>
                                    <th>{t('common.media-type.label')}</th>
                                    <th>{t('common.contact')}</th>
                                    <th>{t('common.intermediary.label')}</th>
                                </tr>
                            </thead>
                            <tbody className="table--event">
                                {eventsByDate.events.map((event) => {
                                    const { id, subject, mediaType, contact, date, type, status, application } = event;

                                    return (
                                        <tr key={id}>
                                            <td>
                                                { }
                                                {EventStatus.NotStarted === status && (
                                                    <MarkEventAsCompleteButton eventId={id} />
                                                )}
                                                {EventStatus.Completed === status && <Checked />}
                                            </td>
                                            <td>
                                                <SSubject isCompleted={EventStatus.Completed === status}>
                                                    {t(`common.event-type.enum.${type}`)}
                                                </SSubject>
                                            </td>
                                            <td>
                                                { }
                                                <Link
                                                    title={t(`common.${type}-subject.enum.${subject}`)}
                                                    to={`/event/${id}`}
                                                >
                                                    <SBoldPrimaryText>
                                                        {t(`common.${type}-subject.enum.${subject}`)}
                                                    </SBoldPrimaryText>
                                                </Link>
                                                <br />
                                                {getTime(date)}
                                            </td>
                                            <td>
                                                <ApplicationSubjectItem application={application} />
                                            </td>
                                            <td>
                                                <ActivityMediaType mediaType={mediaType} />
                                                {renderEventMediaText(event)}
                                            </td>
                                            <td>
                                                {contact && (
                                                    <>
                                                        <Link to={`/contact/${contact.id}`}>
                                                            <SBoldPrimaryText>
                                                                {getContactLabel(contact, t)}
                                                            </SBoldPrimaryText>
                                                        </Link>
                                                        <br />
                                                        <span>{contact.company}</span>
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                {contact?.parent ? (
                                                    <Link to={`/contact/${contact.parent.id}`}>
                                                        {getContactLabel(contact.parent, t)}
                                                    </Link>
                                                ) : (
                                                    <span>{t('common.you')}</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="showOnRowHover">
                                                    <EventActions eventId={id} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </STable>
                    </div>
                ),
            )}
        </div>
    );
};

export default EventList;
